import { Box, Divider, Flex, Heading, Select, Stack, Text, theme } from '@chakra-ui/core';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CorrectAnswerDeterminationType, FreeFormQuestion, JobPosition, MultipleChoiceQuestion, Question } from '../PositionType';

import { FreeFormQuestionsCard } from './FreeFormQuestionsCard';
import { MultipleChoiceQuestionsCard } from './MultipleChoiceQuestion';
import { QuestionsListView } from './QuestionsListView';
import { QuestionsTypes } from './QuestionsTypes';
import { SpecificQuestionsCard } from './SpecificQuestionsCard';
import { useStoreState, useStoreActions } from '../../../../models/hooks';
import {
  QuestionnaireTemplateListResponse,
  QuestionnaireTemplateResponse,
} from '../../questionnaireTemplate/questionnaireTemplate.types';
import { QuestionTemplateSection } from './QuestionnaireTemplateSection';

const initialQuestion = {
  text: '',
  preferredAnswer: true,
  knockout: false,
  isPausable: false,
};

const initialFreeFormQuestion = {
  text: '',
  isActive: true,
  isPausable: false,
};

const initialMultipleChoiceQuestion = {
  question: '',
  images: [],
  knockout: false,
  isPausable: false,
  totalAnswer: 1,
  options: [
    {
      id: '1',
      priority: 0,
      text: '',
    },
    {
      id: '2',
      priority: 1,
      text: '',
    },
  ],

  preferredAnswers: [],
  shouldMatchAtLeast: 1,
  correctAnswerIf: CorrectAnswerDeterminationType.IS_EQUAL,
};
const EMPTY_QUESTION_TEMPLATES = [] as QuestionnaireTemplateListResponse[];

const toListTemplate = (template: QuestionnaireTemplateResponse): QuestionnaireTemplateListResponse => ({
  id: template.id,
  name: template.name,
  account: template.account,
  questionLocale: template.questionLocale,
  default: template.default,
  createdAt: template.createdAt,
  updatedAt: template.updatedAt,
  recruiters: template.recruiters ?? [],
  positionCount: template.positionCount ?? 0,
});

export type QuestionsViewProps = {
  jobPositionData: JobPosition;
  deleteQuestion: (questionId: number) => void;
  deleteFreeFormQuestion: (questionId: number) => void;
  addQuestion: (question: Question, questionEditMode: number | undefined) => void;
  showQuestionForm: boolean;
  addFreeFormQuestion: (question: FreeFormQuestion, questionEditMode: number | undefined) => void;
  addMultipleChoiceQuestion: (question: MultipleChoiceQuestion, questionEditMode: number | undefined) => void;
  deleteMultipleQuestionsQuestion: (questionId: number) => void;
  reOrderedMultipleChoiceQuestions: (reOrderedMultipleChoiceQuestion: MultipleChoiceQuestion[]) => void;
  reOrderSpecificQuestions: (reOrderSpecificQuestions: Question[]) => void;
  reOrderFreeFormQuestions: (reOrderFreeFormQuestions: FreeFormQuestion[]) => void;
  handleQuestionTemplateChange?: (template: QuestionnaireTemplateListResponse | undefined) => void;
  isEditMode?: boolean;
  isCopyingPosition?: boolean;
  selectedQuestionTemplate?: QuestionnaireTemplateListResponse | undefined;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
};

export const QuestionsView = ({
  jobPositionData,
  deleteQuestion,
  addQuestion,
  showQuestionForm,
  addFreeFormQuestion,
  addMultipleChoiceQuestion,
  deleteFreeFormQuestion,
  deleteMultipleQuestionsQuestion,
  reOrderedMultipleChoiceQuestions,
  reOrderSpecificQuestions,
  reOrderFreeFormQuestions,
  handleQuestionTemplateChange,
  isEditMode,
  isCopyingPosition,
  selectedQuestionTemplate,
  scrollContainerRef,
}: QuestionsViewProps): JSX.Element => {
  const { t } = useTranslation();
  const [questionTypes, setQuestionTypes] = useState<QuestionsTypes>(QuestionsTypes.SPECIFIC);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [question, setQuestion] = useState<Question>(initialQuestion);
  const [questionEditMode, setQuestionEditMode] = useState<number | undefined>(undefined);
  const [freeFormQuestion, setFreeFormQuestion] = useState<FreeFormQuestion>(initialFreeFormQuestion);
  const [freeFormQuestionEditMode, setFreeFormQuestionEditMode] = useState<number | undefined>(undefined);
  const [multipleChoiceQuestion, setMultipleChoiceQuestion] = useState<MultipleChoiceQuestion>(initialMultipleChoiceQuestion);
  const [multipleChoiceQuestionEditMode, setMultipleQuestionQuestionEditMode] = useState<number | undefined>(undefined);
  const isQuestionTemplateEnabled = useStoreState((s) => s.app.accounts?.configuration?.questionTemplateEnabled);
  const accountId = useStoreState((s) => s.app.user?.account);
  const getQuestionTemplateById = useStoreActions((actions) => actions.account.getQuestionTemplateById);
  const [questionTemplates, setQuestionTemplates] = useState<QuestionnaireTemplateListResponse[]>(EMPTY_QUESTION_TEMPLATES);
  const [selectedTemplate, setSelectedTemplate] = useState<QuestionnaireTemplateListResponse | undefined>(
    selectedQuestionTemplate ?? undefined,
  );
  /* istanbul ignore next */
  const onRefreshState = () => {
    setQuestion(initialQuestion);
    setQuestionEditMode(undefined);
    setFreeFormQuestionEditMode(undefined);
    setFreeFormQuestion(initialFreeFormQuestion);
    setMultipleChoiceQuestion(initialMultipleChoiceQuestion);
    setMultipleQuestionQuestionEditMode(undefined);
  };
  const scrollPositionRef = useRef<number>(0);

  const saveScrollPosition = () => {
    if (scrollContainerRef?.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
  };

  const restoreScrollPosition = () => {
    const container = scrollContainerRef?.current;
    if (container) {
      container.scrollTop = scrollPositionRef.current;
    }
  };
  const onGetQuestionViewByTypes = () => {
    if (questionTypes === QuestionsTypes.SPECIFIC) {
      return (
        <SpecificQuestionsCard
          jobPositionData={jobPositionData}
          deleteQuestion={deleteQuestion}
          addQuestion={addQuestion}
          showQuestionForm={false}
          questionToEdit={question}
          questionToEditMode={questionEditMode}
          onRefreshState={onRefreshState}
          questionType={questionTypes}
        />
      );
    }
    if (questionTypes === QuestionsTypes.FREEFORM && showQuestionForm) {
      return (
        <FreeFormQuestionsCard
          jobPositionData={jobPositionData}
          deleteFreeFormQuestion={deleteFreeFormQuestion}
          addFreeFormQuestion={addFreeFormQuestion}
          showQuestionForm={showQuestionForm}
          questionToEdit={freeFormQuestion}
          questionToEditMode={freeFormQuestionEditMode}
          onRefreshState={onRefreshState}
          questionType={questionTypes}
        />
      );
    }
    return (
      <MultipleChoiceQuestionsCard
        jobPositionData={jobPositionData}
        addMultipleChoiceQuestion={addMultipleChoiceQuestion}
        deleteMultipleQuestionsQuestion={deleteMultipleQuestionsQuestion}
        questionToEdit={multipleChoiceQuestion}
        questionToEditMode={multipleChoiceQuestionEditMode}
        onRefreshState={onRefreshState}
        questionType={questionTypes}
      />
    );
  };

  const handleChangeQuestionType = (value: QuestionsTypes) => {
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setQuestionTypes(value);
  };

  React.useEffect(
    () => {
      const fetchTemplate = async () => {
        if (jobPositionData?.questionTemplateId && accountId) {
          try {
            const res = await getQuestionTemplateById({ accountId, id: jobPositionData.questionTemplateId });
            if (res) {
              setSelectedTemplate(toListTemplate(res.data));
            }
          } catch (error) {
            console.error('Failed to fetch question template by id', error);
          }
        }
      };

      if (jobPositionData?.questionTemplateId && !selectedTemplate) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchTemplate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobPositionData?.questionTemplateId, accountId, getQuestionTemplateById],
  );

  const hasQuestions =
    (jobPositionData?.specificQuestions?.length || 0) > 0 ||
    (jobPositionData?.freeFormQuestions?.length || 0) > 0 ||
    (jobPositionData?.multipleChoiceQuestions?.length || 0) > 0;

  const renderQuestionTemplateSection = () => (
    <QuestionTemplateSection
      selectedTemplate={selectedTemplate}
      setSelectedTemplate={setSelectedTemplate}
      handleQuestionTemplateChange={handleQuestionTemplateChange}
      questionTemplates={questionTemplates}
      setQuestionTemplates={setQuestionTemplates}
      // jobPositionTemplateId={jobPositionData?.questionTemplateId}
      hasQuestions={hasQuestions}
      saveScrollPosition={saveScrollPosition}
      restoreScrollPosition={restoreScrollPosition}
    />
  );

  const renderQuestionsView = () => (
    <Box>
      <Box
        data-testid="scrollRefElement"
        ref={scrollRef}
        sx={{
          backgroundColor: theme.colors.gray[50],
          padding: 3,
          borderRadius: 'md',
          border: '1px solid',
          borderColor: theme.colors.gray[200],
        }}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Box w="60%">
            <Heading as="h3" fontSize="md" lineHeight="1.2">
              {t('createJobPosition:newQuestionHeader')}
            </Heading>
            <Text fontSize="xs" color="gray.500" lineHeight="normal">
              {t('createJobPosition:questionsHint')}
            </Text>
          </Box>
          <Box pr="6">
            <Select
              variant="outline"
              size="sm"
              borderRadius="md"
              background="white"
              name="question"
              id="questionType"
              value={questionTypes}
              data-testid="selectQuestionType"
              onChange={(e) => handleChangeQuestionType(e.target.value as QuestionsTypes)}
            >
              {showQuestionForm && <option value={QuestionsTypes.FREEFORM}>{t('createJobPosition:freeform')}</option>}
              <option value={QuestionsTypes.SPECIFIC}>{t('createJobPosition:specific')}</option>
              <option value={QuestionsTypes.MULTIPLE_CHOICE}>{t('createJobPosition:multipleChoice')}</option>
            </Select>
          </Box>
        </Flex>
        <Divider my={2} />
        {onGetQuestionViewByTypes()}
      </Box>
      {!jobPositionData?.questionTemplateId && (
        <QuestionsListView
          jobPositionData={jobPositionData}
          editQuestion={(index: number, que: Question) => {
            setQuestionTypes(QuestionsTypes.SPECIFIC);
            setQuestion(que);
            setQuestionEditMode(index);
            /* istanbul ignore else */
            if (scrollRef.current) {
              scrollRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          editFreeFormQuestion={(index: number, que: FreeFormQuestion) => {
            setQuestionTypes(QuestionsTypes.FREEFORM);
            setFreeFormQuestion(que);
            setFreeFormQuestionEditMode(index);
            /* istanbul ignore else */
            if (scrollRef.current) {
              scrollRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          deleteQuestion={deleteQuestion}
          deleteFreeFormQuestion={deleteFreeFormQuestion}
          editMultipleChoiceQuestion={(index: number, que: MultipleChoiceQuestion) => {
            setQuestionTypes(QuestionsTypes.MULTIPLE_CHOICE);
            setMultipleChoiceQuestion({ ...que });
            setMultipleQuestionQuestionEditMode(index);
            /* istanbul ignore else */
            if (scrollRef.current) {
              scrollRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          deleteMultipleQuestionsQuestion={deleteMultipleQuestionsQuestion}
          reOrderMultiChoiceQuestions={reOrderedMultipleChoiceQuestions}
          reOrderSpecificQuestions={reOrderSpecificQuestions}
          reOrderFreeFormQuestions={reOrderFreeFormQuestions}
        />
      )}
    </Box>
  );

  if (!isQuestionTemplateEnabled) {
    return renderQuestionsView();
  }

  const hasTemplateId = Boolean(jobPositionData?.questionTemplateId);
  const showBothSections = (isEditMode || isCopyingPosition) && !hasTemplateId && hasQuestions;

  if (showBothSections) {
    return (
      <Stack spacing={4}>
        {renderQuestionTemplateSection()}
        {renderQuestionsView()}
      </Stack>
    );
  }
  return renderQuestionTemplateSection();
};
